<!-- Pick Goods Page -->
<div data-testid="pick-goods-page" class="p-6 pick-goods-page">
  <!-- Header -->
  <div
    class="mb-6 pb-4 border-b border-gray-200 flex justify-between items-center"
  >
    <h2 class="text-2xl font-semibold text-gray-800">
      {{ "outbound_management.pick_goods.title" | translate }}
    </h2>
    <div class="flex gap-2">
      <app-button
        [testId]="'pick-goods-cancel-btn'"
        label="common.actions.cancel"
        type="secondary"
        [outlined]="true"
        (emitOnClick)="onCancel()"
      ></app-button>
      <app-button
        [testId]="'pick-goods-confirm-btn'"
        label="common.actions.confirm"
        type="info"
        (emitOnClick)="onConfirm()"
        [disabled]="pickLines.length === 0"
      ></app-button>
    </div>
  </div>

  <!-- Main Content: Two Columns -->
  <div
    class="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6 pb-6 border-b border-gray-200"
  >
    <!-- Left Column: List of items by ODO (Read-only) -->
    <div class="lg:col-span-7 bg-white p-4">
      <div class="flex justify-between items-center mb-4">
        <h4 class="text-lg font-semibold text-gray-800">
          {{ "outbound_management.pick_goods.odo_list" | translate }}
        </h4>
        <app-button
          [testId]="'pick-goods-pick-all-btn'"
          label="outbound_management.pick_goods.pick_all_odo"
          type="info"
          icon="add"
          (emitOnClick)="onPickAllODO()"
          [disabled]="odoListDataSource.length === 0"
        ></app-button>
      </div>
      @if (odoListDataSource.length === 0 && !loading && items.length > 0) {
        <!-- Custom empty message when all items are picked -->
        <div
          class="p-8 text-center bg-gray-50 border border-gray-200 rounded-lg"
        >
          <i class="pi pi-check-circle text-4xl text-green-500 mb-3"></i>
          <p class="text-gray-600 font-medium">
            {{ "outbound_management.pick_goods.all_items_picked" | translate }}
          </p>
        </div>
      } @else {
        <div class="odo-list-table">
          <app-data-table
            data-testid="pick-goods-odo-list-table"
            [columns]="odoListColumns"
            [dataSource]="odoListDataSource"
            [loading]="loading"
            [hasCheckColumn]="false"
            [hasSerialColumn]="false"
            [totalRecords]="odoListDataSource.length"
            [first]="0"
            [rows]="odoListDataSource.length"
            [showPaginator]="false"
            [scrollHeight]="'400px'"
          ></app-data-table>
        </div>
      }
    </div>

    <!-- Right Column:  pick goods area -->
    <div class="lg:col-span-5 bg-white p-4">
      <form
        data-testid="pick-goods-tracking-line-form"
        [formGroup]="addTrackingLineForm"
        class="space-y-4 add-tracking-line-form"
      >
        <!-- Barcode Input -->
        <div>
          <label
            for="pg-barcode"
            class="block text-sm font-medium text-gray-700 mb-1"
          >
            {{ "inbound.receive.scan_barcode" | translate }}
          </label>
          <input
            data-testid="pick-goods-barcode-input"
            type="text"
            pInputText
            id="pg-barcode"
            formControlName="barcode"
            [(ngModel)]="formBarcodeInput"
            [placeholder]="'inbound.receive.barcode_placeholder' | translate"
            class="w-full text-lg py-3"
            [disabled]="scanningBarcode"
            (keyup.enter)="onFormBarcodeInput()"
          />
        </div>

        <!-- Product Select -->
        <div>
          <label
            for="pg-product"
            class="block text-sm font-medium text-gray-700 mb-1"
          >
            {{ "inbound.fields.product_name" | translate }}
          </label>
          <p-select
            data-testid="pick-goods-product-select"
            [options]="getProductOptions()"
            formControlName="product"
            inputId="pg-product"
            [placeholder]="'inbound.receive.select_product' | translate"
            [showClear]="true"
            [filter]="true"
            filterBy="skuName,skuCode"
            [filterPlaceholder]="
              'common.search.form.actions.search' | translate
            "
            [emptyFilterMessage]="
              'common.messages.no_results_found' | translate
            "
            [emptyMessage]="'common.messages.no_results_found' | translate"
            styleClass="w-full"
            [class.p-invalid]="
              addTrackingLineForm.get('product')?.invalid &&
              addTrackingLineForm.get('product')?.touched
            "
          >
            <ng-template let-item pTemplate="item">
              <div>{{ item.skuName || item.skuCode }}</div>
            </ng-template>
            <ng-template let-item pTemplate="selectedItem">
              <div>{{ item.skuName || item.skuCode }}</div>
            </ng-template>
          </p-select>
          <small
            class="text-red-500 text-sm mt-1 block"
            [class.invisible]="
              !(
                addTrackingLineForm.get('product')?.invalid &&
                addTrackingLineForm.get('product')?.touched
              )
            "
            [style.min-height.rem]="1.25"
          >
            {{ "common.validation.required" | translate }}
          </small>
        </div>

        <!-- Lot, Serial, Quantity Row -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
          @if (selectedProduct?.policy?.requireLot) {
          <div>
            <label
              for="pg-lot"
              class="block text-sm font-medium mb-1 text-gray-700"
            >
              {{ "inbound.fields.lot" | translate }}
              <span class="text-red-500">*</span>
            </label>
            <p-select
              data-testid="pick-goods-lot-select"
              [options]="lotChoices()"
              formControlName="lot"
              optionLabel="label"
              optionValue="value"
              inputId="pg-lot"
              [placeholder]="'inbound.receive.select_lot' | translate"
              styleClass="w-full"
              [filter]="true"
              filterBy="label"
            >
            </p-select>
          </div>
          }

          <!-- Serial Number - Disabled -->
          <div>
            <label
              for="pg-serial"
              class="block text-sm font-medium text-gray-500 mb-1"
            >
              {{ "inbound.fields.serial" | translate }}
            </label>
            <p-select
              data-testid="pick-goods-serial-select"
              [options]="[]"
              formControlName="serial"
              optionLabel="label"
              optionValue="value"
              inputId="pg-serial"
              [placeholder]="'inbound.fields.serial' | translate"
              styleClass="w-full"
              [disabled]="true"
              [filter]="true"
              [filterBy]="'label'"
              [filterPlaceholder]="
                'common.search.form.actions.search' | translate
              "
              [emptyFilterMessage]="
                'common.messages.no_results_found' | translate
              "
              [emptyMessage]="'common.messages.no_results_found' | translate"
            >
            </p-select>
          </div>

          <!-- Quantity -->
          <div>
            <label
              for="pg-quantity"
              class="block text-sm font-medium text-gray-700 mb-1"
            >
              {{ "inbound.fields.quantity" | translate }}
            </label>
            <input
              data-testid="pick-goods-quantity-input"
              type="number"
              pInputText
              id="pg-quantity"
              formControlName="quantity"
              [min]="0"
              class="w-full"
              [class.p-invalid]="
                addTrackingLineForm.get('quantity')?.invalid &&
                addTrackingLineForm.get('quantity')?.touched
              "
            />
            <small
              class="text-red-500 text-sm mt-1 block"
              [class.invisible]="
                !(
                  addTrackingLineForm.get('quantity')?.invalid &&
                  addTrackingLineForm.get('quantity')?.touched
                )
              "
              [style.min-height.rem]="1.25"
            >
              @if (
                addTrackingLineForm.get("quantity")?.invalid &&
                addTrackingLineForm.get("quantity")?.touched
              ) {
                @if (
                  addTrackingLineForm.get("quantity")?.errors?.["required"]
                ) {
                  {{ "common.validation.required" | translate }}
                } @else if (
                  addTrackingLineForm.get("quantity")?.errors?.["min"]
                ) {
                  {{ "common.validation.min_value" | translate: { min: 1 } }}
                } @else if (
                  addTrackingLineForm.get("quantity")?.errors?.["maxQuantity"]
                ) {
                  {{
                    "outbound_management.pick_goods.validation.quantity_exceeds_remaining"
                      | translate
                        : {
                            max:
                              addTrackingLineForm.get("quantity")?.errors?.[
                                "maxQuantity"
                              ]?.max || 0,
                          }
                  }}
                }
              }
            </small>
          </div>
        </div>
      </form>

      <!-- Action Buttons - Right aligned -->
      <div class="flex justify-end gap-2 mt-4">
        @if (editingTrackingLineId) {
          <app-button
            [testId]="'pick-goods-save-line-btn'"
            [label]="'common.actions.save' | translate"
            type="info"
            icon="add"
            (emitOnClick)="onAddTrackingLine()"
            [disabled]="
              !addTrackingLineForm.get('product')?.value ||
              !addTrackingLineForm.get('quantity')?.value
            "
          ></app-button>
          <app-button
            [testId]="'pick-goods-cancel-edit-btn'"
            [label]="'common.actions.cancel' | translate"
            type="secondary"
            [outlined]="true"
            (emitOnClick)="onCancelEdit()"
          ></app-button>
        } @else {
          <app-button
            [testId]="'pick-goods-add-line-btn'"
            [label]="'common.actions.add' | translate"
            type="info"
            icon="add"
            (emitOnClick)="onAddTrackingLine()"
            [disabled]="
              !addTrackingLineForm.get('product')?.value ||
              !addTrackingLineForm.get('quantity')?.value
            "
          ></app-button>
        }
      </div>
    </div>
  </div>

  <!-- Tracking Lines Table -->
  <div class="mb-4">
    <h3 class="text-lg font-semibold text-gray-800 mb-3">
      {{ "inbound.dialog.tracking_lines" | translate }}
      @if (pickLineGroups.length > 0) {
        <span class="text-sm font-normal text-gray-500">
          ({{ pickLines.length }})
        </span>
      }
    </h3>

    @if (pickLineGroups.length === 0) {
      <div class="p-8 text-center bg-gray-50 border border-gray-200 rounded-lg">
        <i class="pi pi-inbox text-4xl text-gray-400 mb-3"></i>
        <p class="text-gray-500">
          {{ "inbound.dialog.no_tracking_lines" | translate }}
        </p>
        <p class="text-sm text-gray-400 mt-2">
          {{ "inbound.dialog.scan_barcode_to_add" | translate }}
        </p>
      </div>
    } @else {
      <!-- Use app-data-table component -->
      <app-data-table
        data-testid="pick-goods-lines-table"
        [columns]="tableColumns"
        [dataSource]="tableDataSource"
        [loading]="loading"
        [hasCheckColumn]="false"
        [hasSerialColumn]="false"
        [totalRecords]="tableDataSource.length"
        [first]="0"
        [rows]="tableDataSource.length"
      >
        <!-- Custom template for group headers and actions -->
        <ng-template
          #customTemplate
          let-value
          let-rowData="rowData"
          let-column="column"
        >
          @if (rowData.isGroupHeader) {
            @if (column.prop === "skuName") {
              <div class="flex items-center gap-2 text-left">
                <button
                  [attr.data-testid]="'pick-goods-toggle-group-' + rowData.odoCode"
                  type="button"
                  class="pi cursor-pointer flex-shrink-0 bg-transparent border-none p-0"
                  [class.pi-chevron-down]="rowData.isExpanded"
                  [class.pi-chevron-right]="!rowData.isExpanded"
                  (click)="toggleSkuGroup(rowData.skuCode)"
                ></button>
                <span
                  class="sku-name-cell truncate"
                  [pTooltip]="value"
                  tooltipPosition="top"
                  tooltipStyleClass="custom-tooltip"
                  [showDelay]="300"
                  [hideDelay]="0"
                >
                  {{ value }}
                </span>
              </div>
            } @else if (column.prop === "lot") {
              <span>-</span>
            } @else if (column.prop === "serial") {
              <span>-</span>
            } @else if (column.prop === "quantity") {
              <span class="text-right">{{ rowData.totalQuantity }}</span>
            } @else if (column.prop === "plannedQty") {
              <span class="text-right">{{ rowData.plannedQty || "" }}</span>
            } @else if (column.prop === "pickedQty") {
              <span class="text-right">{{ rowData.pickedQty || "" }}</span>
            } @else if (column.prop === "action") {
              <div class="flex justify-center action-cell">
                <app-button
                  [testId]="'pick-goods-action-delete-group-' + rowData.odoCode"
                  class="action-delete"
                  [label]="'common.actions.delete' | translate"
                  type="action"
                  (emitOnClick)="removeSkuGroup(rowData.skuCode)"
                ></app-button>
              </div>
            } @else {
              <span>{{ value }}</span>
            }
          } @else {
            @if (column.prop === "skuName") {
              <span
                class="sku-name-cell truncate"
                [class.editing-row-marker]="rowData.isEditing"
                [attr.data-editing]="rowData.isEditing ? 'true' : null"
                [pTooltip]="value"
                tooltipPosition="top"
                tooltipStyleClass="custom-tooltip"
                [showDelay]="300"
                [hideDelay]="0"
              >
                {{ value }}
              </span>
            } @else if (column.prop === "lot") {
              <span class="text-left">{{ rowData.lot || "" }}</span>
            } @else if (column.prop === "serial") {
              <span class="text-left">{{ rowData.serial || "" }}</span>
            } @else if (column.prop === "quantity") {
              <span class="text-right">{{ rowData.quantity || 0 }}</span>
            } @else if (column.prop === "plannedQty") {
              <span class="text-right">{{ rowData.plannedQty || "" }}</span>
            } @else if (column.prop === "pickedQty") {
              <span class="text-right">{{ rowData.pickedQty || "" }}</span>
            } @else if (column.prop === "action") {
              @if (rowData.isEditing) {
                <div class="flex justify-center action-cell">
                  <span class="text-gray-400 text-sm"></span>
                </div>
              } @else {
                <div class="flex justify-center gap-2 action-cell">
                  <app-button
                    [testId]="'pick-goods-action-edit-' + rowData.id"
                    class="action-edit"
                    [label]="'common.actions.edit' | translate"
                    type="action"
                    (emitOnClick)="onEditTrackingLine(rowData)"
                  ></app-button>
                  <app-button
                    [testId]="'pick-goods-action-delete-' + rowData.id"
                    class="action-delete"
                    [label]="'common.actions.delete' | translate"
                    type="action"
                    (emitOnClick)="removeTrackingLine(rowData)"
                  ></app-button>
                </div>
              }
            } @else {
              <span>{{ value }}</span>
            }
          }
        </ng-template>
      </app-data-table>
    }
  </div>
</div>

<p-toast data-testid="pick-goods-toast"></p-toast>

<app-fefo-override-dialog
  [(visible)]="fefoOverrideVisible"
  [data]="fefoOverrideData"
  (result)="onFefoOverrideResult($event)"
></app-fefo-override-dialog>
